/**
 * Workspace - RPC schemas for per-project workspace orchestration.
 *
 * Currently exposes `workspace.prepareAgentWorktree`, which creates a git
 * worktree from a project and resolves the AI coding agent (Claude Code,
 * Codex, ...) that will run in the terminal launched on top of that worktree.
 */
import { Schema } from "effect";
import { ProjectId, ThreadId, TrimmedNonEmptyString } from "./baseSchemas";
import { GitWorktreeBase } from "./git";

const AgentIdInputSchema = TrimmedNonEmptyString.check(Schema.isMaxLength(64));

export const WorkspacePrepareAgentWorktreeInput = Schema.Struct({
  projectId: ProjectId,
  agentId: Schema.optional(AgentIdInputSchema),
  baseBranch: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(255))),
  sessionId: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(128))),
});
export type WorkspacePrepareAgentWorktreeInput = typeof WorkspacePrepareAgentWorktreeInput.Type;

const AgentLaunchSpecSchema = Schema.Struct({
  id: TrimmedNonEmptyString,
  label: TrimmedNonEmptyString,
  command: TrimmedNonEmptyString,
  args: Schema.Array(Schema.String),
  env: Schema.Record(Schema.String, Schema.String),
});
export type AgentLaunchSpec = typeof AgentLaunchSpecSchema.Type;

export const SetupHookSchema = Schema.Struct({
  commands: Schema.Array(Schema.String.check(Schema.isMaxLength(4_096))).check(
    Schema.isMaxLength(32),
  ),
  commandTimeoutMs: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1_000)).check(
    Schema.isLessThanOrEqualTo(3_600_000),
  ),
});
export type SetupHook = typeof SetupHookSchema.Type;

export const WorkspacePrepareAgentWorktreeResult = Schema.Struct({
  worktreePath: TrimmedNonEmptyString,
  branch: TrimmedNonEmptyString,
  baseBranch: TrimmedNonEmptyString,
  projectWorkspaceRoot: TrimmedNonEmptyString,
  agent: AgentLaunchSpecSchema,
  setupHook: Schema.NullOr(SetupHookSchema),
  /**
   * The ref the thread's branch actually started from. `source: "local"` means
   * the remote copy of `baseBranch` could not be used, so the thread may have
   * started from code that is behind the remote.
   */
  base: Schema.optional(GitWorktreeBase),
});
export type WorkspacePrepareAgentWorktreeResult = typeof WorkspacePrepareAgentWorktreeResult.Type;

/**
 * `workspace.prepareThreadWorktree` — create the git worktree for a regular
 * chat thread.
 *
 * Same engine as `prepareAgentWorktree` (project git credentials, the server's
 * `worktreesDir`, `baseSource: "remote"`, the project's `onWorktreeCreate`
 * hook), minus the agent: no `AgentLaunchSpec`, no PTY. The client dispatches
 * `thread.create` with the returned `worktreePath`/`branch`.
 *
 * `branchName` is the optional user-supplied name — typically the same text
 * that becomes the thread's title (which has no length cap of its own, e.g.
 * a Plane issue's `ref: title`), not a pre-shortened slug. It is sanitized
 * and truncated to the eventual branch fragment length server-side, so the
 * cap here only needs to bound the raw wire payload, not the git branch name
 * itself — it must stay well above the sanitizer's 64-char slug limit or a
 * normal-length title fails validation outright. That failure doesn't even
 * surface as a clean rejection: a request whose body fails schema decoding
 * can't have its `id` recovered by the caller, so it silently misses the
 * pending call it was answering and the client just times out 60s later.
 * Omitting `branchName` keeps the historical behaviour: a throwaway
 * `taskin/<hex8>` branch, which is what makes the first-turn LLM branch
 * rename kick in (see `ProviderCommandReactor`).
 */
export const WorkspacePrepareThreadWorktreeInput = Schema.Struct({
  projectId: ProjectId,
  threadId: ThreadId,
  baseBranch: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(255))),
  branchName: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(255))),
});
export type WorkspacePrepareThreadWorktreeInput = typeof WorkspacePrepareThreadWorktreeInput.Type;

export const WorkspacePrepareThreadWorktreeResult = Schema.Struct({
  worktreePath: TrimmedNonEmptyString,
  branch: TrimmedNonEmptyString,
  baseBranch: TrimmedNonEmptyString,
  projectWorkspaceRoot: TrimmedNonEmptyString,
  setupHook: Schema.NullOr(SetupHookSchema),
  /** See {@link WorkspacePrepareAgentWorktreeResult.base}. */
  base: Schema.optional(GitWorktreeBase),
});
export type WorkspacePrepareThreadWorktreeResult = typeof WorkspacePrepareThreadWorktreeResult.Type;

/**
 * `workspace.ensureAgentThreadTerminal` — idempotently ensure that the agent
 * PTY session for a thread is alive.
 *
 * Used when re-opening a thread whose terminal session may have died (server
 * restart, app reopen). If the thread has an `associatedAgentId` and
 * `associatedWorktreePath`, the server (re)launches the agent with
 * `--resume <threadId>` into the `default` terminal for that thread.
 *
 * Returns `relaunched: false` when the session is already live, when the
 * thread is not an agent-worktree thread, or when required metadata is
 * missing.
 */
export const WorkspaceEnsureAgentThreadTerminalInput = Schema.Struct({
  threadId: ThreadId,
});
export type WorkspaceEnsureAgentThreadTerminalInput =
  typeof WorkspaceEnsureAgentThreadTerminalInput.Type;

export const WorkspaceEnsureAgentThreadTerminalResult = Schema.Struct({
  relaunched: Schema.Boolean,
  reason: Schema.Literals(["not-agent-thread", "already-running", "relaunched-with-resume"]),
});
export type WorkspaceEnsureAgentThreadTerminalResult =
  typeof WorkspaceEnsureAgentThreadTerminalResult.Type;

/**
 * `workspace.runProjectScript` — run a project action in the background.
 *
 * The client sends identifiers only, never a command: the server resolves the
 * action from the project's persisted `scripts` and refuses anything that is
 * not `kind: "terminal"` + `runMode: "background"`. That is what keeps this
 * from being a general-purpose "run arbitrary shell" endpoint for users who
 * were deliberately not given terminal access.
 *
 * Returns as soon as the process is spawned; progress and output arrive on the
 * `workspace.projectScriptStatus` push channel.
 */
export const WorkspaceRunProjectScriptInput = Schema.Struct({
  threadId: ThreadId,
  projectId: ProjectId,
  scriptId: TrimmedNonEmptyString.check(Schema.isMaxLength(64)),
});
export type WorkspaceRunProjectScriptInput = typeof WorkspaceRunProjectScriptInput.Type;

export const WorkspaceRunProjectScriptResult = Schema.Struct({
  runId: TrimmedNonEmptyString.check(Schema.isMaxLength(64)),
  scriptName: TrimmedNonEmptyString,
  command: TrimmedNonEmptyString,
  cwd: TrimmedNonEmptyString,
  startedAt: TrimmedNonEmptyString,
});
export type WorkspaceRunProjectScriptResult = typeof WorkspaceRunProjectScriptResult.Type;
